import { useCallback, useEffect } from 'react';

import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { type CalculatorEmergency } from '@models/Calculator';
import { useForm } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { Navigate } from 'react-router';
import { z } from 'zod';

import { useCalculator } from '../../context';
import Content from '../content';

const vasoactiveDrugs: CalculatorEmergency = {
  name: 'Drogas Vasoativas',
  sections: [
    {
      title: 'NORADRENALINA',
      content: {
        addFactorSixtyToCalculate: true,
        addFactorThousandToCalculate: true,
        addWeightToCalculate: true,
        ampouleDefault: 4,
        concentrationUnitMeasure: 'mg/mL',
        currentFlowDefault: 0,
        doseDecimalPlaces: 2,
        doseUnitMeasure: 'mcg/Kg/min',
        drugDefault: JSON.stringify({
          mg: 4,
          ml: 4,
        }),
        drugOptions: [
          {
            label: '4mg/4mL',
            value: JSON.stringify({
              mg: 4,
              ml: 4,
            }),
          },
        ],
        maxDoseDefault: 2,
        minDoseDefault: 0.05,
        observations: ['Após 0,7mcg/Kg/min, associar vasopressina'],
        salineDefault: 250,
        salineLabel: 'ml de SG 5%',
        suggestions: [
          '4 amp + 250mL SG 5% (Acesso central) ou 1 amp + 250mL SG 5% (Acesso periférico)',
          'Dose mínima: 0,05mcg/Kg/min',
          'Dose máxima: 2mcg/Kg/min',
        ],
        title: 'Noradrenalina',
      },
    },
    {
      title: 'DOBUTAMINA',
      content: {
        addFactorSixtyToCalculate: true,
        addFactorThousandToCalculate: true,
        addWeightToCalculate: true,
        ampouleDefault: 1,
        concentrationUnitMeasure: 'mg/mL',
        currentFlowDefault: 0,
        doseUnitMeasure: 'mcg/Kg/min',
        drugDefault: JSON.stringify({
          mg: 250,
          ml: 20,
        }),
        drugOptions: [
          {
            label: '250mg/20mL',
            value: JSON.stringify({
              mg: 250,
              ml: 20,
            }),
          },
        ],
        maxDoseDefault: 20,
        minDoseDefault: 2,
        salineDefault: 230,
        salineLabel: 'ml de SF 0,9%',
        suggestions: [
          '1 amp + 230mL SF 0,9%',
          'Dose mínima: 2mcg/Kg/min',
          'Dose máxima: 20mcg/Kg/min',
        ],
        title: 'Dobutamina',
      },
    },
    {
      title: 'VASOPRESSINA',
      content: {
        addFactorSixtyToCalculate: true,
        addFactorThousandToCalculate: false,
        addWeightToCalculate: false,
        ampouleDefault: 1,
        concentrationUnitMeasure: 'U/mL',
        currentFlowDefault: 0,
        doseDecimalPlaces: 2,
        doseUnitMeasure: 'U/min',
        drugDefault: JSON.stringify({
          mg: 20,
          ml: 1,
        }),
        drugOptions: [
          {
            label: '20U/1mL',
            value: JSON.stringify({
              mg: 20,
              ml: 1,
            }),
          },
        ],
        maxDoseDefault: 0.04,
        minDoseDefault: 0.01,
        salineDefault: 100,
        salineLabel: 'ml de SF 0,9%',
        suggestions: [
          '1 amp + 100mL SF 0,9%',
          'Dose mínima: 0,01U/min',
          'Dose máxima: 0,04U/min',
        ],
        title: 'Vasopressina',
      },
    },
    {
      title: 'ADRENALINA',
      content: {
        addFactorSixtyToCalculate: true,
        addFactorThousandToCalculate: true,
        addWeightToCalculate: false,
        ampouleDefault: 6,
        concentrationUnitMeasure: 'mg/mL',
        currentFlowDefault: 0,
        doseDecimalPlaces: 2,
        doseUnitMeasure: 'mcg/min',
        drugDefault: JSON.stringify({
          mg: 1,
          ml: 1,
        }),
        drugOptions: [
          {
            label: '1mg/1mL',
            value: JSON.stringify({
              mg: 1,
              ml: 1,
            }),
          },
        ],
        maxDoseDefault: 20,
        minDoseDefault: 1,
        salineDefault: 100,
        salineLabel: 'ml de SF 0,9%',
        suggestions: [
          '6 amp + 100mL SF 0,9%',
          'Dose mínima: 1mcg/min',
          'Dose máxima: 20mcg/min',
        ],
        title: 'Adrenalina',
      },
    },
    {
      title: 'DOPAMINA',
      content: {
        addFactorSixtyToCalculate: true,
        addFactorThousandToCalculate: true,
        addWeightToCalculate: true,
        ampouleDefault: 5,
        concentrationUnitMeasure: 'mg/mL',
        currentFlowDefault: 0,
        doseUnitMeasure: 'mcg/Kg/min',
        drugDefault: JSON.stringify({
          mg: 50,
          ml: 10,
        }),
        drugOptions: [
          {
            label: '50mg/10mL',
            value: JSON.stringify({
              mg: 50,
              ml: 10,
            }),
          },
        ],
        maxDoseDefault: 20,
        minDoseDefault: 2,
        observations: [
          'Dose cronotrópica (Beta) = 2 a 10mcg/Kg/min',
          'Dose vasopressora (Alfa) = 10 a 20mcg/Kg/min',
        ],
        salineDefault: 200,
        salineLabel: 'ml de SG 5%',
        suggestions: [
          '5 amp + 200mLSG 5%',
          'Dose mínima: 2mcg/Kg/min',
          'Dose máxima: 20mcg/Kg/min',
        ],
        title: 'Dopamina',
      },
    },
  ],
};

const formSchema = z.object({
  weight: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function VasoactiveDrugs() {
  const { user } = useAuth();
  const { setWeight, weight } = useCalculator();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      weight: weight.toFixed(1),
    },
  });

  function onSubmit(): void {
    //
  }

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    setWeight(70);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Drogas vasoativas" />
      <Form {...form}>
        <form
          className="bg-background sticky top-0 z-[9] flex justify-center py-4"
          onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="weight"
            render={({ field: { ref, ...rest } }) => (
              <FormItem className="flex flex-row items-center">
                <FormLabel>Peso</FormLabel>
                <FormControl>
                  <NumberFormatBase
                    className="[&_input]:w-26 [&_input]:text-center"
                    customInput={Input}
                    format={(value) => formatterDecimal(value, 1)}
                    getCaretBoundary={(formattedValue) => {
                      const boundaryAry = Array.from({
                        length: formattedValue.length + 1,
                      }).map(() => true);
                      boundaryAry[0] = false;
                      boundaryAry[1] = false;
                      boundaryAry[2] = false;
                      return boundaryAry;
                    }}
                    getInputRef={ref}
                    {...rest}
                  />
                </FormControl>
                <FormDescription>Kg</FormDescription>
              </FormItem>
            )}
          />
        </form>
      </Form>
      <div>
        {vasoactiveDrugs.sections.map((item) => (
          <Content
            addFactorSixtyToCalculate={item.content.addFactorSixtyToCalculate}
            addFactorThousandToCalculate={
              item.content.addFactorThousandToCalculate
            }
            addWeightToCalculate={item.content.addWeightToCalculate}
            ampouleDefault={item.content.ampouleDefault}
            applicationUnitMeasure={item.content.applicationUnitMeasure}
            concentrationUnitMeasure={item.content.concentrationUnitMeasure}
            currentFlowDefault={item.content.currentFlowDefault}
            diluteDefault={item.content.diluteDefault}
            doseDecimalPlaces={item.content.doseDecimalPlaces}
            doseDefault={item.content.doseDefault}
            doseUnitMeasure={item.content.doseUnitMeasure}
            drugDefault={item.content.drugDefault}
            drugOptions={item.content.drugOptions}
            key={item.title}
            maxDoseDefault={item.content.maxDoseDefault}
            minDoseDefault={item.content.minDoseDefault}
            observations={item.content.observations}
            salineDefault={item.content.salineDefault}
            salineLabel={item.content.salineLabel}
            suggestions={item.content.suggestions}
            templateJustDoseAndApplication={
              item.content.templateJustDoseAndApplication
            }
            templateJustDoseAndApplication2={
              item.content.templateJustDoseAndApplication2
            }
            title={item.title}
          />
        ))}
      </div>
    </div>
  );
}

export default VasoactiveDrugs;
